import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BackToLoginButton } from "@/features/auth/components/LoginForm/Forms/FormParts/BackToLoginButton";
import { LoginFormError } from "@/features/auth/components/LoginForm/Forms/FormParts/LoginFormError";
import { SecurityStepHeader } from "@/features/auth/components/LoginForm/Forms/FormParts/SecurityStepHeader";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { type MissingCellphoneFormData, missingCellphoneFormSchema } from "@/features/auth/schemas/forms/missingCellphoneForm.schema";
import { countryData } from "@/features/userProfile/constants/countryData";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";
import { ControlledSelect } from "@/shared/components/forms/ControlledSelect";
import { ControlledSwitch } from "@/shared/components/forms/ControlledSwitch";
import { ControlledTextInput } from "@/shared/components/forms/ControlledTextInput";
import { P } from "@/shared/ui/typographies/paragraph";

const defaultValues: MissingCellphoneFormData = {
  cell_phone: "",
  dialing_code: "+33",
  allow_show_cell_phone: false,
  allow_use_cell_phone: false,
};

type MissingCellphoneFormProps = {
  loginData: {
    email: string
    password: string
  }
  onBackToLogin: () => void
};

export const MissingCellphoneForm = ({ loginData, onBackToLogin }: MissingCellphoneFormProps) => {
  const {
    isCellPhoneRequired, error, clearError, registerPhone,
  } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<MissingCellphoneFormData>({
    resolver: zodResolver(missingCellphoneFormSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const onSubmitForm = async (formData: MissingCellphoneFormData) => {
    clearError();
    const { dialing_code, ...rest } = formData;
    await registerPhone({
      ...rest,
      indicatif: dialing_code.replace(/^\+/, ""),
      username: loginData.email,
      password: loginData.password,
    });
  };

  if (!isCellPhoneRequired) return null;

  return (
    <div className="space-y-6">
      <SecurityStepHeader
        title="Votre numéro de portable"
        description="Il sert à sécuriser votre connexion par un code de vérification. Les autorisations ci-dessous restent facultatives."
      />
      <form
        onSubmit={handleSubmit(onSubmitForm)}
        className="space-y-8 overflow-visible"
      >
        <LoginFormError title={error.title} description={error.description} />

        <div className="flex flex-col gap-4">
          <ControlledSelect<MissingCellphoneFormData>
            id="dialing_code"
            label="Indicatif"
            name="dialing_code"
            control={control}
            wrapperClassName="space-y-2 flex flex-col"
            options={countryData.map((country) => ({
              value: country.dialingCode,
              label: `${country.flag} ${country.name} (${country.dialingCode})`,
            }))}
            dataTestId="dialing-code"
          />

          <ControlledTextInput<MissingCellphoneFormData>
            id="cellphone-input"
            label="Numéro de téléphone"
            type="tel"
            name="cell_phone"
            placeholder="0600000000"
            control={control}
            dataTestId="cellphone-input"
            inputMode="numeric"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <P className="text-sm">J'autorise mon CSE / groupement à :</P>
          <ControlledSwitch<MissingCellphoneFormData>
            id="allow_show_cell_phone"
            name="allow_show_cell_phone"
            control={control}
            dataTestId="allow_show_cell_phone"
            label="voir mon numéro"
          />

          <ControlledSwitch<MissingCellphoneFormData>
            id="allow_use_cell_phone"
            name="allow_use_cell_phone"
            control={control}
            dataTestId="allow_use_cell_phone"
            label="me contacter par SMS"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <BackToLoginButton onClick={onBackToLogin} />
          <ButtonWithLoader
            type="submit"
            data-testid="cellphone-submit-button"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            color="primary"
            pill
          >
            {isSubmitting ? "Enregistrement en cours..." : "Enregistrer"}
          </ButtonWithLoader>
        </div>
      </form>
    </div>
  );
};
